package com.karyadina.ui.theme

import androidx.compose.foundation.interaction.MutableInteractionSource
import androidx.compose.foundation.interaction.PressInteraction
import androidx.compose.foundation.indication
import androidx.compose.material3.ripple
import androidx.compose.runtime.*
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.graphicsLayer
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.dp
import kotlinx.coroutines.flow.collect

/**
 * Elevation and interaction utility functions for premium polish
 */
object ElevationUtils {
    
    // Elevation levels for visual hierarchy
    object Levels {
        val SUBTLE: Dp = 2.dp      // Minimal elevation for interactive elements
        val STANDARD: Dp = 4.dp    // Default card elevation
        val RAISED: Dp = 6.dp      // Prominent cards (stats, CTAs)
        val FLOATING: Dp = 8.dp    // Floating action buttons, modals
        val MODAL: Dp = 12.dp      // Dialog overlays
    }
    
    /**
     * Get ripple effect modifier for interactive surfaces
     * @param color Optional ripple color (defaults to theme primary)
     * @param bounded Whether ripple is bounded to surface
     */
    fun Modifier.rippleEffect(
        color: Color? = null,
        bounded: Boolean = true
    ): Modifier {
        val interactionSource = MutableInteractionSource()
        return this.indication(
            interactionSource = interactionSource,
            indication = ripple(bounded = bounded)
        )
    }
    
    /**
     * Create interaction source for tracking press interactions
     * Useful for scale animations and haptic feedback
     */
    fun createInteractionSource() = MutableInteractionSource()
    
    /**
     * Apply scale animation on press interaction
     * Scales element to 95% when pressed, 100% when released
     * @param interactionSource Source tracking press interactions
     */
    @Composable
    fun Modifier.scaleOnPress(
        interactionSource: MutableInteractionSource
    ): Modifier {
        var isPressed by remember { mutableStateOf(false) }
        
        LaunchedEffect(interactionSource) {
            interactionSource.interactions.collect { interaction ->
                when (interaction) {
                    is PressInteraction.Press -> isPressed = true
                    is PressInteraction.Release -> isPressed = false
                    is PressInteraction.Cancel -> isPressed = false
                }
            }
        }
        
        return this.graphicsLayer {
            scaleX = if (isPressed) 0.95f else 1f
            scaleY = if (isPressed) 0.95f else 1f
        }
    }
}

/**
 * Elevation hierarchy helpers
 */
object ElevationProfile {
    
    /**
     * Subtle elevation for secondary interactive elements
     * Examples: Unselected tabs, badge icons, minor card layers
     */
    val subtleElevation: Dp
        get() = ElevationUtils.Levels.SUBTLE
    
    /**
     * Standard elevation for primary content cards
     * Examples: Flashcard items, subject cards, review containers
     */
    val standardElevation: Dp
        get() = ElevationUtils.Levels.STANDARD
    
    /**
     * Raised elevation for prominent/featured content
     * Examples: Statistics cards, quick action cards, CTA sections
     */
    val raisedElevation: Dp
        get() = ElevationUtils.Levels.RAISED
    
    /**
     * Floating elevation for above-surface elements
     * Examples: Floating action buttons, floating chips, popups
     */
    val floatingElevation: Dp
        get() = ElevationUtils.Levels.FLOATING
    
    /**
     * Modal elevation for overlay dialogs
     * Examples: Alert dialogs, bottom sheets, full modal overlays
     */
    val modalElevation: Dp
        get() = ElevationUtils.Levels.MODAL
}
